import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { notificationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck, Calendar, ChevronLeft, ThumbsUp, LogOut, CreditCard, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Notifications = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.warn("Notifications error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkRead(notification.notification_id);
    }
    // Don't navigate for waitlist notifications that need response
    if (notification.type === 'waitlist_new_date') return;
    
    // Route based on notification type
    if (notification.type === 'new_message') {
      navigate('/messages');
    } else if (notification.type === 'payment_proof_received' && notification.activity_id) {
      navigate(`/dashboard?activity=${notification.activity_id}&tab=pending`);
    } else if (notification.type === 'payment_confirmed' || notification.type === 'waitlist_promoted') {
      navigate('/my-activities');
    } else if (notification.type === 'payment_not_found') {
      const creatorId = notification.creator_id;
      const activityId = notification.activity_id;
      if (creatorId && activityId) {
        navigate(`/messages?to=${creatorId}&activity=${activityId}`);
      } else {
        navigate('/my-activities');
      }
    } else if (notification.type === 'review_reminder' && notification.activity_id) {
      navigate(`/activity/${notification.activity_id}?review=true`);
    } else if (notification.type === 'waitlist_payment_required' || notification.type === 'payment_reminder_24h') {
      navigate(`/checkout/${notification.activity_id}`);
    } else if (notification.type === 'payment_expired') {
      navigate('/prelanzamiento');
    } else if (notification.activity_id) {
      navigate(`/activity/${notification.activity_id}`);
    }
  };

  const handleWaitlistRespond = async (notification, response) => {
    setRespondingTo(notification.notification_id);
    try {
      await axios.post(
        `${API}/api/activities/${notification.activity_id}/waitlist/respond`,
        { response, action_id: notification.action_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response === 'interested') {
        toast.success('¡Tu interés ha sido registrado!');
      } else {
        toast.success('Has salido de la lista de espera');
      }
      setNotifications(prev => prev.map(n =>
        n.notification_id === notification.notification_id
          ? { ...n, waitlist_response: response, read: true }
          : n
      ));
    } catch (e) {
      toast.error('Error al responder');
    } finally {
      setRespondingTo(null);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="skeleton w-16 h-16 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 bg-[#FAFAFA]">
      <div className="max-w-2xl mx-auto">
        {/* Sticky Header */}
        <div className="sticky top-14 md:top-11 z-30 bg-[#FAFAFA] pt-4 px-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="font-cabinet text-3xl font-black text-slate-900">
                {t('notif.title')}
              </h1>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleMarkAllRead}
                className="rounded-full text-xs whitespace-nowrap flex-shrink-0 flex"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                {t('notif.markAllRead')}
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="px-4">
        {notifications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-900/10">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t('notif.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <button
                key={notification.notification_id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${
                  notification.read 
                    ? 'bg-white border-slate-900/10' 
                    : 'bg-[#22BCB9]/5 border-[#22BCB9]/20'
                }`}
                data-testid={`notification-${notification.notification_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full ${notification.read ? 'bg-slate-100' : 'bg-[#22BCB9]/10'}`}>
                    {notification.type === 'new_activity_date' || notification.type === 'waitlist_new_date' ? (
                      <Calendar className={`w-5 h-5 ${notification.read ? 'text-slate-500' : 'text-[#22BCB9]'}`} />
                    ) : notification.type === 'waitlist_no_date' ? (
                      <Bell className={`w-5 h-5 ${notification.read ? 'text-slate-500' : 'text-amber-500'}`} />
                    ) : notification.type === 'waitlist_payment_required' || notification.type === 'payment_reminder_24h' ? (
                      <CreditCard className={`w-5 h-5 ${notification.read ? 'text-slate-500' : notification.type === 'payment_reminder_24h' ? 'text-amber-500' : 'text-[#22BCB9]'}`} />
                    ) : notification.type === 'payment_expired' ? (
                      <AlertTriangle className={`w-5 h-5 ${notification.read ? 'text-slate-500' : 'text-red-500'}`} />
                    ) : notification.type === 'payment_proof_received' ? (
                      <CreditCard className={`w-5 h-5 ${notification.read ? 'text-slate-500' : 'text-blue-500'}`} />
                    ) : (
                      <Bell className={`w-5 h-5 ${notification.read ? 'text-slate-500' : 'text-[#22BCB9]'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${notification.read ? 'text-slate-700' : 'text-slate-900'}`}>
                      {notification.title}
                    </p>
                    <p className="text-sm text-slate-500 line-clamp-2">{notification.message}</p>
                    
                    {/* Waitlist response buttons */}
                    {notification.type === 'waitlist_new_date' && !notification.waitlist_response && (
                      <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
                        <Button
                          size="sm"
                          onClick={() => handleWaitlistRespond(notification, 'interested')}
                          disabled={respondingTo === notification.notification_id}
                          className="bg-primary hover:bg-primary-hover text-white rounded-full text-xs h-8 px-4"
                          data-testid={`waitlist-interested-${notification.notification_id}`}
                        >
                          <ThumbsUp className="w-3 h-3 mr-1" />
                          Me interesa
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleWaitlistRespond(notification, 'leave')}
                          disabled={respondingTo === notification.notification_id}
                          className="rounded-full text-xs h-8 px-4 text-gray-500"
                          data-testid={`waitlist-leave-${notification.notification_id}`}
                        >
                          <LogOut className="w-3 h-3 mr-1" />
                          Salir de la lista
                        </Button>
                      </div>
                    )}
                    {notification.type === 'waitlist_new_date' && notification.waitlist_response === 'interested' && (
                      <p className="text-xs text-green-600 mt-2 font-medium">Respondiste: Me interesa</p>
                    )}
                    {notification.type === 'waitlist_new_date' && notification.waitlist_response === 'leave' && (
                      <p className="text-xs text-gray-400 mt-2">Saliste de la lista de espera</p>
                    )}
                    
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-[#22BCB9] flex-shrink-0 mt-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
